import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject,combineLatest } from 'rxjs';
import { takeUntil, debounceTime, map } from 'rxjs/operators';
import { ABPService } from 'src/app/abp-management/abp.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';

@Component({
  selector: 'app-post-info',
  templateUrl: './post-info.component.html',
  styleUrls: ['./post-info.component.scss'],
})
export class PostInfoComponent implements OnInit, OnDestroy {
  @Input() postInfoGroup: FormGroup;
  @Input() closeAbpUtmFormEvent: EventEmitter<void>;
  @Input() type: string | undefined;
  @Input() scheduleGroup: FormGroup;
  @ViewChild('desc') desc: ElementRef;
  
  ctaOptions: any[] = [];
  /** True while CTA options are being fetched from the API. */
  ctaOptionsLoading = false;
  /** When true, the showcases API returned items (other locations have showcases at the same dates). */
  showSameDatesWarning = false;

  chipsArray = ['city', 'full address', 'state', 'location name'];
  showCtaURLField = false;
  unsubscribe$ = new Subject<void>();
  showAbpUtmForm = false;
  public ctaURL: string;
  public minDate = new Date();
  /** Minimum selectable start date: 2 days from today. Only dates on or after this are shown for start date. */
  public get minStartDate(): Date {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d;
  }
  /** Minimum selectable end date: start date + 1 day (end date must be after start date). */
  public get minEndDate(): Date {
    const startDate = this.postInfoGroup?.get('startDate')?.value;
    if (startDate) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + 1);
      return d;
    }
    return this.minDate;
  }
  public urlToolTip: string = `Must be a valid URL.`;
  
  private readonly JWT_SECRET = '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a';
  private readonly API_BASE_URL = 'https://io.renderseo.com';
  private lastLocationKey: string = ''; // Track last API call to avoid duplicates
  private lastShowcasesKey: string = ''; // Track last showcases API call
  /** Selected account id (same as listings: from getSelectedAccount$()). */
  private currentAccountId: number;
  /** Current user login id (same as listings: from getCurrentUser$()). */
  private userLoginId: number;

  constructor(
    private abpService: ABPService,
    private http: HttpClient,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    // Fetch account and user the same way as listings / abp-post-locations
    this.sessionService.getSelectedAccount$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((account) => {
        this.currentAccountId = account?._id;
      });
    this.sessionService.getCurrentUser$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id;
      });

    if (this.closeAbpUtmFormEvent) {
      this.closeAbpUtmFormEvent.subscribe(() => this.onCloseAbpUtmForm());
    }
    this.showAbpUtmForm = false;
    this.postInfoGroup?.controls['ctaURL']?.enable();
    
    if (this.type != 'offer') {
      if (this.postInfoGroup.controls.addButton.value !== 'CALL') {
        this.showCtaURLField = !!this.postInfoGroup.controls.addButton.value;
      }
      this.postInfoGroup.controls.addButton.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.showCtaURLField = value === 'CALL' ? false : !!value;
        });
    }

    // Set language if it's value is there and is in format of `en-US / fr-CA / es-ES`
    if (
      this.postInfoGroup.controls['languageCode']?.value &&
      ((this.postInfoGroup.controls['languageCode']?.value === 'en-US') ||
        (this.postInfoGroup.controls['languageCode']?.value === 'fr-CA') ||
        (this.postInfoGroup.controls['languageCode']?.value === 'es-ES'))
    ) {
      this.postInfoGroup.controls['languageCode'].setValue(
        this.abpService.preparePostLanguage(this.postInfoGroup.controls['languageCode'].value)
      );
    }

    // Setup location watchers with a delay to ensure scheduleGroup is initialized
    setTimeout(() => {
      this.setupLocationWatchers();
      this.setupDateWatchers();
    }, 100);
  }


    private formatDateForAPI(date: Date | string): string | null {
    if (!date) {
      return null;
    }

    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date:', date);
      return null;
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

    private fetchShowcases(): void {
    const startDate = this.postInfoGroup?.controls['startDate']?.value;
    const endDate = this.postInfoGroup?.controls['endDate']?.value;

    if (!startDate || !endDate) {
      console.log('Start date or end date not selected, skipping showcases API call');
      this.showSameDatesWarning = false;
      return;
    }

    if (!this.scheduleGroup) {
      console.warn('scheduleGroup not available for showcases API call');
      return;
    }

    // Format dates as YYYY-MM-DD
    const formattedStartDate = this.formatDateForAPI(startDate);
    const formattedEndDate = this.formatDateForAPI(endDate);

    if (!formattedStartDate || !formattedEndDate) {
      console.error('Error formatting dates');
      return;
    }

    // Extract location IDs
    const isSelectedAll = this.scheduleGroup.controls.isSelectedAll?.value || false;
    const selectedLocations = this.scheduleGroup.controls.location?.value || [];
    
    // If all locations are selected, skip the API call
    if (isSelectedAll) {
      console.log('All locations selected, skipping showcases API call');
      return;
    }
    
    // Only proceed if there are filtered locations (not all locations)
    let locationIds: string[] = [];
    
    if (selectedLocations.length > 0) {
      // Extract location IDs from location arrays
      locationIds = selectedLocations
        .map((loc: any) => {
          if (Array.isArray(loc)) {
            return loc[0]?.toString();
          } else if (loc?._id) {
            return loc._id.toString();
          }
          return null;
        })
        .filter(id => id !== null && id !== undefined);
    }

    if (locationIds.length === 0) {
      console.warn('No locations selected, cannot fetch showcases');
      return;
    }

    // Create key to check for duplicate calls
    const showcasesKey = `${formattedStartDate}_${formattedEndDate}_${locationIds.sort().join(',')}`;
    
    // Skip if same parameters as last call
    if (showcasesKey === this.lastShowcasesKey) {
      console.log('Same showcases parameters as last call, skipping API request');
      return;
    }
    this.lastShowcasesKey = showcasesKey;

    // Build API URL
    const locationsParam = locationIds.join(',');
    const apiUrl = `${this.API_BASE_URL}/apple/showcases?start_date=${formattedStartDate}&end_date=${formattedEndDate}&locations=${locationsParam}`;

    // Use account and user ids the same way as listings
    const userId = this.userLoginId;
    const accountId = this.currentAccountId;

    if (!userId || !accountId) {
      console.warn('User ID or Account ID not available for showcases API call', { userId, accountId });
      return;
    }

    // Generate JWT token
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18 seconds
    };

    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);

    console.log('Calling Showcases API:', apiUrl);
    console.log('Authorization Token:', jwtToken);
    console.log('Request Headers:', { Authorization: jwtToken });

    // Make API call
    this.http.get<any>(apiUrl, { headers }).subscribe(
      (response) => {
        console.log('[Showcases GET API] URL:', apiUrl);
        console.log('[Showcases GET API] Response:', response);
        console.log('[Showcases GET API] Response (JSON):', JSON.stringify(response, null, 2));

        // Response may be { statusCode, body, headers } or { items: [...] }
        let items: any[] = [];
        if (response?.items && Array.isArray(response.items)) {
          items = response.items;
        } else if (response?.body) {
          try {
            const body = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
            items = body?.items && Array.isArray(body.items) ? body.items : [];
          } catch {
            items = [];
          }
        }
        this.showSameDatesWarning = items.length > 0;
      },
      (error) => {
        console.error('Error fetching showcases:', error);
        if (error.status === 404) {
          console.warn('Showcases not found for selected dates and locations');
        }
        this.showSameDatesWarning = false;
      }
    );
  }

  /**
   * Setup watchers for location changes
   */
  private setupLocationWatchers(): void {
    if (!this.scheduleGroup) {
      console.warn('scheduleGroup not available in post-info component');
      return;
    }

    // Watch for location array changes
    if (this.scheduleGroup.controls.location) {
      this.scheduleGroup.controls.location.valueChanges
        .pipe(
          takeUntil(this.unsubscribe$),
          debounceTime(300)
        )
        .subscribe(() => {
          console.log('Location array changed:', this.scheduleGroup.controls.location.value);
          this.fetchCtaOptions();
        });
    }

    // Watch for "Select All" checkbox changes
    if (this.scheduleGroup.controls.isSelectedAll) {
      this.scheduleGroup.controls.isSelectedAll.valueChanges
        .pipe(
          takeUntil(this.unsubscribe$),
          debounceTime(300)
        )
        .subscribe((isSelectedAll) => {
          console.log('Select All changed:', isSelectedAll);
          // When select all changes, wait a bit for location array to update, then fetch
          setTimeout(() => {
            this.fetchCtaOptions();
          }, 100);
        });

      // Initial fetch if locations are already selected
      const hasLocations = this.scheduleGroup.controls.location?.value?.length > 0;
      const isAllSelected = this.scheduleGroup.controls.isSelectedAll.value;
      
      if (hasLocations || isAllSelected) {
        setTimeout(() => {
          this.fetchCtaOptions();
        }, 200);
      }
    }
  }


    private setupDateWatchers(): void {
    const startDateControl = this.postInfoGroup?.controls['startDate'];
    const endDateControl = this.postInfoGroup?.controls['endDate'];

    if (!startDateControl || !endDateControl) {
      console.warn('Start date or end date controls not found');
      return;
    }

    // Watch for both start date and end date changes
    combineLatest([
      startDateControl.valueChanges,
      endDateControl.valueChanges
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(300),
        map(([startDate, endDate]) => ({
          startDate,
          endDate
        }))
      )
      .subscribe(({ startDate, endDate }) => {
        console.log('Date changed - startDate:', startDate, 'endDate:', endDate);
        
        // Only call API if both dates are selected
        if (startDate && endDate) {
          this.fetchShowcases();
        }
      });

    // Initial check if both dates are already selected
    if (startDateControl.value && endDateControl.value) {
      setTimeout(() => {
        this.fetchShowcases();
      }, 200);
    }
  }

  /**
   * Fetch CTA options from API based on selected locations
   */
  private fetchCtaOptions(): void {
    if (!this.scheduleGroup) {
      return;
    }

    const isSelectedAll = this.scheduleGroup.controls.isSelectedAll?.value || false;
    const selectedLocations = this.scheduleGroup.controls.location?.value || [];
    
    console.log('Fetching CTA options - isSelectedAll:', isSelectedAll, 'locations:', selectedLocations.length);
    
    // Extract location IDs
    let locationIds: string[] = [];
    let locationKey: string = '';
    
    // When "select all" is checked, the location array should contain all locations
    // So we extract IDs from the array regardless of isSelectedAll flag
    if (selectedLocations.length > 0) {
      // Extract location IDs from location arrays (location[0] is the _id)
      locationIds = selectedLocations
        .map((loc: any) => {
          // Handle array format [id, storeCode, businessName, city, state, ...]
          if (Array.isArray(loc)) {
            return loc[0]?.toString();
          } 
          // Handle object format {_id: ..., storeCode: ..., ...}
          else if (loc?._id) {
            return loc._id.toString();
          }
          return null;
        })
        .filter(id => id !== null && id !== undefined);
      
      locationKey = isSelectedAll ? 'all' : locationIds.sort().join(',');
    } else {
      // No locations selected, clear CTA options
      console.log('No locations selected, clearing CTA options');
      this.ctaOptions = [];
      this.lastLocationKey = '';
      return;
    }

    // Skip if same locations as last call
    if (locationKey === this.lastLocationKey && this.ctaOptions.length > 0) {
      console.log('Same locations as last call, skipping API request');
      return;
    }
    this.lastLocationKey = locationKey;

    // Build API URL
    // If all locations are selected, don't include locations parameter
    // Otherwise, include locations parameter
    let apiUrl: string;
    
    if (isSelectedAll) {
      // All locations selected - call API without locations parameter
      apiUrl = `${this.API_BASE_URL}/apple/actionlinks`;
    } else {
      // Specific locations selected - include locations parameter
      const locationsParam = locationIds.join(',');
      apiUrl = `${this.API_BASE_URL}/apple/actionlinks?locations=${locationsParam}`;
    }

    // Use account and user ids the same way as listings
    const userId = this.userLoginId;
    const accountId = this.currentAccountId;

    console.log('Preparing to call CTA API with URL:', this.currentAccountId);

    if (!userId || !accountId) {
      console.warn('User ID or Account ID not available for CTA API call', { userId, accountId });
      return;
    }

    // Generate JWT token
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18 seconds
    };

    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);

    console.log('Calling CTA API:', apiUrl);
    console.log('Authorization Token:', jwtToken);
    console.log('Request Headers:', { Authorization: jwtToken });

    this.ctaOptionsLoading = true;

    // Make API call
    this.http.get<any>(apiUrl, { headers }).subscribe(
      (response) => {
        this.ctaOptionsLoading = false;
        console.log('CTA API response:', response);
        // Map response to dropdown options
        // Response format: { cta_summary: { "SCHEDULE": 235, "MENU": 1, ... } }
        if (response?.cta_summary) {
          this.ctaOptions = Object.keys(response.cta_summary)
            .filter(key => response.cta_summary[key] > 0) // Only include CTAs with count > 0
            .map(key => ({
              value: key,
              label: this.formatCtaLabel(key),
            }))
            .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
          
          console.log('CTA options populated:', this.ctaOptions.length, 'options');
        } else {
          console.warn('No cta_summary in response');
          this.ctaOptions = [];
        }
      },
      (error) => {
        this.ctaOptionsLoading = false;
        console.error('Error fetching CTA options:', error);
        // Don't clear options on error - keep previous options if available
        if (error.status === 404) {
          console.warn('CTA summary not found for selected locations');
        }
        // Only clear if we don't have any options yet
        if (this.ctaOptions.length === 0) {
          this.ctaOptions = [];
        }
      }
    );
  }

  /**
   * Format CTA key to readable label
   */
  private formatCtaLabel(key: string): string {
    // Convert "SCHEDULE" to "Schedule", "LEARN_MORE" to "Learn More", etc.
    return key
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  }

  onCloseAbpUtmForm(): void {
    this.showAbpUtmForm = false;
    this.postInfoGroup?.controls['ctaURL']?.enable();
  }

  onChipClick(text: string) {
    this.desc.nativeElement.value =
      this.desc.nativeElement.value.substr(0, this.desc.nativeElement.selectionStart) +
      `{${text}}` +
      this.desc.nativeElement.value.substr(
        this.desc.nativeElement.selectionStart,
        this.desc.nativeElement.value.length
      );
    this.postInfoGroup.patchValue({
      postDescription: this.desc.nativeElement.value,
    });
    this.desc.nativeElement.focus();
  }

  handleInputCtaURLChange(event): void {
    const value = event.target.value;
    this.ctaURL = value;
    this.postInfoGroup.patchValue({
      ctaURL: value,
    });
  }

  public handleDropdownChange(event): void {
    if (event?.value) {
      this.postInfoGroup.controls['ctaURL'].addValidators(Validators.required);
    }
  }

  public get descriptionCharacterLimit(): number {
    return this.desc?.nativeElement?.value?.length;
  }

  public get titleCharacterLimit(): number {
    return this.postInfoGroup.controls['title']?.value.length;
  }

  handleCtaUrlWithUtmChange(event: Event): void {
    this.postInfoGroup.patchValue({
      ctaURL: event,
    });
    this.ctaURL = event as unknown as string;
  }

  public onDropdownChange(): void {
    if (this.postInfoGroup.controls['addButton'].value === 'CALL') {
      this.postInfoGroup.controls['ctaURL'].setValue('');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}